import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, formatDate, formatTime, rupiah } from "@/lib/api";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/StatusBadge";
import { WhatsAppButton } from "@/components/WhatsAppButton";

function usePoll(fn, ms, deps) {
  useEffect(() => {
    fn();
    const id = setInterval(fn, ms);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const ThreadList = ({ threads, activeId }) => (
  <div className="flex flex-col" data-testid="chat-thread-list">
    {threads.length === 0 && <p className="p-6 text-sm text-stone-500" data-testid="chat-list-empty">Belum ada percakapan. Chat dimulai setelah penawaran dikirim.</p>}
    {threads.map((t) => (
      <Link key={t.thread_id} to={`/pesan/${t.thread_id}`} data-testid={`chat-thread-${t.thread_id}`}
        className={cn("flex items-start gap-3 border-b border-stone-100 px-5 py-4 transition-colors hover:bg-stone-50", activeId === t.thread_id && "bg-teal-50/60")}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-stone-200 text-sm font-bold text-stone-700">{t.other_name[0]}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-bold text-stone-900">{t.other_name}</p>
            <span className="shrink-0 text-[11px] text-stone-400">{formatDate(t.last_at, { day: "numeric", month: "short" })}</span>
          </div>
          <p className="text-xs text-teal-700">{t.category_name}</p>
          <p className="mt-0.5 truncate text-xs text-stone-500">{t.last_message}</p>
        </div>
        {t.unread > 0 && <span className="rounded-full bg-teal-700 px-1.5 text-[11px] font-bold text-white" data-testid={`chat-thread-unread-${t.thread_id}`}>{t.unread}</span>}
      </Link>
    ))}
  </div>
);

const Thread = ({ threadId, userId, role }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/chats/${threadId}`).then((r) => setData(r.data)).catch(() => navigate("/pesan")), [threadId, navigate]);
  usePoll(load, 4000, [load]);
  const count = data?.messages.length;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [count]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/chats/${threadId}/messages`, { text });
      setText("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!data) return <div className="flex-1" />;
  const t = data.thread;
  const reqLink = t.order_id ? `/pesanan/${t.order_id}` : role === "pelanggan" ? `/permintaan/${t.request_id}` : `/dasbor/permintaan-masuk/${t.request_id}`;
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="chat-thread-view">
      <div className="flex flex-col gap-3 border-b border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/pesan" className="mb-1 inline-flex items-center gap-1 text-xs text-stone-500 md:hidden"><ArrowLeft className="h-3 w-3" /> Semua pesan</Link>
          <p className="font-bold text-stone-900" data-testid="chat-other-name">{t.other_name}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-stone-500">
            <Link to={reqLink} className="text-teal-700 hover:underline" data-testid="chat-request-link">{t.category_name}</Link>
            {data.price && <span>· {rupiah(data.price)}</span>}
            {(data.order_status || data.quote_status) && <StatusBadge status={data.order_status || data.quote_status} testId="chat-quote-status" className="py-0.5" />}
          </div>
        </div>
        <WhatsAppButton whatsapp={data.whatsapp} testId="chat-whatsapp-button" />
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto bg-stone-50/60 px-5 py-6" data-testid="chat-messages">
        {data.messages.map((m) => {
          const mine = m.sender_id === userId;
          return (
            <div key={m.message_id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
              <div data-testid={`chat-message-${m.message_id}`}
                className={cn("max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed", mine ? "rounded-br-md bg-teal-700 text-white" : "rounded-bl-md border border-stone-200 bg-white text-stone-800")}>
                <p className="whitespace-pre-line">{m.text}</p>
                <p className={cn("mt-1 text-[10px]", mine ? "text-teal-100" : "text-stone-400")}>{formatTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-2 border-t border-stone-200 bg-white p-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Tulis pesan…" data-testid="chat-input"
          className="h-11 flex-1 rounded-xl border border-stone-200 bg-stone-50 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40" />
        <button type="submit" data-testid="chat-send-button" className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-700 text-white transition-[background-color,transform] hover:bg-teal-800 active:scale-95">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};

export default function Chats() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  usePoll(() => api.get("/chats").then((r) => setThreads(r.data)).catch(() => {}), 5000, []);

  return (
    <div className="container-page py-8" data-testid="chats-page">
      <h1 className="mb-6 text-3xl font-bold tracking-tight text-stone-900">Pesan</h1>
      <div className="card-soft flex h-[calc(100vh-14rem)] min-h-[480px] overflow-hidden">
        <aside className={cn("w-full overflow-y-auto border-r border-stone-200 md:w-80 md:shrink-0", threadId && "hidden md:block")}>
          <ThreadList threads={threads} activeId={threadId} />
        </aside>
        {threadId ? <Thread key={threadId} threadId={threadId} userId={user.user_id} role={user.role} /> : (
          <div className="hidden flex-1 flex-col items-center justify-center gap-2 text-stone-400 md:flex">
            <MessagesSquare className="h-8 w-8" /> <p className="text-sm">Pilih percakapan</p>
          </div>
        )}
      </div>
    </div>
  );
}
